import _get from 'lodash/get';
import _set from 'lodash/set';
import _cloneDeep from 'lodash/cloneDeep';
import _isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import I18n from 'i18n-js';
import { ajax } from 'rxjs/ajax';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  CASH_BY_RECIPIENT,
  CASH_BY_SENDER,
  PAYMENT_TYPE_GRABPAY,
} from 'constants/booking-constants';
import {
  codAdditionalServiceID,
  TIME_FORMAT,
  DEFAULT_TIME_FORMAT,
  isBulkUploadDeliveryID,
  genDeliveryID,
  ErrorCodes,
} from 'constants/anytime-booking-constants';
import { getV2QuotesEpt } from 'actions/booking-endpoints';
import {
  enterpriseSelector,
  isPaxSelector,
  selectedCitySelector,
  quoteRequestConfigSelector,
} from 'selectors/app-selector';
import { buildHeadersV2, buildAjaxCredentials } from 'helper/ajax-helper';
import { getMexCreateDeliveryInfo, isFromMexApp } from 'helper/mex-app-helper';
import _every from 'lodash/every';
import _some from 'lodash/some';
import _includes from 'lodash/includes';

import { getInsuranceOptionID } from './insurance';

export const getDeliveryFinalDropOffs = deliveryDetail => {
  if (!deliveryDetail) {
    return undefined;
  }
  const origDropOffs = _get(deliveryDetail, 'dropOffs', []);
  if (!deliveryDetail.isRoundTrip) {
    return origDropOffs;
  }
  const pickUpStep = _get(deliveryDetail, 'pickUp', {});
  return [...origDropOffs, pickUpStep];
};

export const dateKeyFormat = 'YYYY-MM-DD';

export const isRewardOffer = offerType =>
  ['OT_REWARD', 'USER_REWARD'].includes(offerType);

export const isAutoApplyOfferType = offerType =>
  [
    'ST_DEFAULT',
    'ST_GRABPAY',
    'ST_GRABPAY_CREDIT',
    'ST_AUTO_APPLY_REDEMPTION',
    'ST_NO_OFFERS_PROMO',
  ].includes(offerType);

export const getOfferID = offer => _get(offer, 'offerID') || _get(offer, 'id');

export const getOfferType = offer =>
  _get(offer, 'offerType') || _get(offer, 'type');

const timeFormat = countryCode =>
  _get(TIME_FORMAT, countryCode, DEFAULT_TIME_FORMAT);

const getDiscountInfo = (detail, isCreatingDelivery = false) => {
  const offerConfig = _get(detail, 'configuration.offerConfig');

  if (!offerConfig) {
    return null;
  }
  const offerID = getOfferID(offerConfig);
  const offerType = getOfferType(offerConfig);
  if (!isCreatingDelivery) {
    if (!offerID || isAutoApplyOfferType(offerType)) {
      // Don't send discount in getQuote if the offerConfig is Auto Promo
      return null;
    }
  }

  const isReward = isRewardOffer(offerConfig.offerType);

  const discountInfo = {
    id: offerID,
    type: offerConfig.offerType,
    name: offerConfig.name,
    uuid: isReward
      ? _get(offerConfig, 'redemptionMeta.redemptionUUID', null)
      : null,
  };

  return discountInfo;
};

export const humanizeTimestamp = ({ timestamp, countryCode }) => {
  const time = moment(timestamp * 1000);
  const isToday = time.isSame(new Date(), 'day');
  return {
    dateLabel: isToday
      ? I18n.t('v2.anytimeBooking.today')
      : time.format(dateKeyFormat),
    timeLabel: time.format(timeFormat(countryCode)),
  };
};

export const processTimeSlot = ({
  timeSlot,
  pickUpSLA = 0,
  dropOffSLA = 0,
  totalSLA = 0,
  isBestOption,
  countryCode,
}) => {
  if (!(timeSlot && timeSlot.from && timeSlot.to)) {
    const now = moment();
    const dateKey = now.format(dateKeyFormat);
    const pickUpSLAInMin = pickUpSLA / 60;
    return {
      dateKey,
      dateLabels: {
        weekday: I18n.t('v2.anytimeBooking.today'),
        date: now.format('DD'),
      },
      pickUpRange: I18n.t('v2.anytimeBooking.pickUpNowWithSLA', {
        pickUpSLAInMin,
      }),
      dropOffTime: totalSLA
        ? now.add(totalSLA * 1000).format(timeFormat(countryCode))
        : undefined,
    };
  }

  const from = moment(timeSlot.from);
  const to = moment(timeSlot.to);
  const dateKey = from.format(dateKeyFormat);
  const isToday = from.isSame(new Date(), 'day');
  const shortWeekday = isToday
    ? I18n.t('v2.anytimeBooking.today')
    : I18n.t(`v2.anytimeBooking.weekDayShort.${from.format('ddd')}`);
  const longWeekday = isToday
    ? I18n.t('v2.anytimeBooking.today')
    : from.format('ddd');
  const pickUpRange = `${from.format(timeFormat(countryCode))} - ${to.format(
    timeFormat(countryCode),
  )}`;
  const longPickUpRange = isBestOption
    ? `${longWeekday}, ${pickUpRange}`
    : pickUpRange;
  return {
    dateKey,
    dateLabels: {
      weekday: shortWeekday,
      date: from.format('DD'),
    },
    pickUpRange: longPickUpRange,
    dropOffTime: to.add(dropOffSLA * 1000).format(timeFormat(countryCode)),
  };
};

export const generateDisplayPickUpTimeAndDropOffTime = (
  serviceType,
  specialService,
  timeSlot,
  countryCode,
) => {
  if (timeSlot && timeSlot.from && timeSlot.to) {
    const dropOffSLA = _get(serviceType, 'metadata.dropOffSLA', 0);
    const { pickUpRange, dropOffTime, dateLabels } = processTimeSlot({
      timeSlot,
      dropOffSLA,
      countryCode,
    });
    return {
      pickUpTime: `${pickUpRange}, ${dateLabels.weekday}`,
      dropOffTime,
    };
  }
  const totalSLA =
    _get(serviceType, 'metadata.pickUpSLA', 0) +
    _get(serviceType, 'metadata.estimatedTimeAllocation', 0) + // for SD only
    _get(serviceType, 'metadata.dropOffSLA', 0);
  const dropOffTime = moment()
    .add(totalSLA * 1000)
    .format(timeFormat(countryCode));
  return {
    pickUpTime: serviceType.pickUpTime,
    dropOffTime,
  };
};

export const mergeServiceTypeAndSpecialServiceType = (
  serviceType,
  specialService,
  disabled = false,
  disabledReason = undefined,
  disabledHint = undefined,
) => ({
  key: `${serviceType.id}-${specialService.id}`,
  serviceType: serviceType.id,
  specialServiceType: specialService.id,
  icon: specialService.icon || serviceType.icon || '',
  name:
    (specialService.id !== 'None' && specialService.name) ||
    serviceType.serviceTypeName,
  description: specialService.description || serviceType.description,
  fare: specialService.fare,
  recommendedVehicleType: specialService.recommendedVehicleType,
  surgeDisplay: specialService.surgeDisplay,
  displayPosition: specialService.displayPosition || 999,
  priceIndicator: specialService.priceIndicator,
  disabled,
  disabledReason,
  disabledHint,
});

const makeItineraryItemForGetQuotesV2 = (
  isPickUp,
  codValid,
  isBulkUpload,
  totalWeight = 0,
  itemType = undefined,
) => (step, index) => {
  if (!step) {
    return null;
  }

  const { poi, cod, insuranceOption } = step;
  if (!poi) {
    return null;
  }

  const poiMeta = _get(poi, 'metadata');
  const searchUUID = _get(poiMeta, 'uuid');
  const searchMetadataAPI = _get(poiMeta, 'api');

  const place = {
    id: poi.id,
    details: {
      address: poi.address.combined_address,
      keyword: poi.address.name,
    },
    coordinate: poi.latlng,
    cityID: poi.address.cityId,
    countryID: poi.address.country_id,
    searchUUID,
    searchMetadataAPI,
  };

  const cashOnDelivery =
    isPickUp || !codValid || isNaN(cod)
      ? undefined
      : {
          enable: true,
          amount: +cod,
        };

  let parcelInfo;
  if (!isPickUp) {
    if (isBulkUpload) {
      parcelInfo = parseParcelInfo(
        _get(step, 'itemType'),
        _get(step, 'itemWeight'),
      );
    } else if (index === 0) {
      parcelInfo = parseParcelInfo(itemType, totalWeight);
    } else {
      parcelInfo = parseParcelInfo(itemType, 0);
    }
  }

  return {
    place,
    cashOnDelivery,
    parcelInfo,
    insuranceOption: insuranceOption ? { id: insuranceOption.id } : undefined,
  };
};

const parseParcelInfo = (itemType, parcelWeight) => {
  const itemTypeName = itemType || '';
  const [catID, catName] = itemTypeName.split('-');

  return {
    itemCategory: {
      id: catID ? +catID : 0,
      name: catName || '',
    },
    itemInfo: {
      weight: parcelWeight || 0,
    },
  };
};

export const optOutAutoApplyOffer = deliveryDetail => {
  const preventAutoApplyOffer = _get(
    deliveryDetail,
    'autoApplyOffer.preventAutoApplyOffer',
  );
  return !_every([
    deliveryDetail.configuration.serviceType,
    deliveryDetail.pickUp,
    !_isEmpty(deliveryDetail.dropOffs),
    !preventAutoApplyOffer,
  ]);
};

const deliveryToGetQuoteV2Payload = enterprise => delivery => {
  const { detail } = delivery;
  if (!detail) {
    return null;
  }

  const enableOptimizedRoute = _get(
    delivery,
    'routeOptimization.enabled',
    false,
  );

  const dropOffs = _get(detail, 'dropOffs', []);

  // Bulk: use from step, UI: use from detail.itemWeight
  let itemType, totalWeight;
  const isBulkUpload = isBulkUploadDeliveryID(delivery.id);
  if (isBulkUpload) {
    totalWeight = dropOffs.reduce(
      (partialWeight, step) => partialWeight + _get(step, 'itemWeight', 0),
      0,
    );
  } else {
    totalWeight = isNaN(detail.itemWeight) ? 0 : +detail.itemWeight;
    itemType = detail.itemType;
  }
  const codValid = !!detail.configuration.additionalServices.find(
    as => as.id === codAdditionalServiceID,
  );

  const baseConfiguration = {
    itinerary: [
      makeItineraryItemForGetQuotesV2(true, undefined, isBulkUpload)(
        _get(detail, 'pickUp'),
      ),
      ...getDeliveryFinalDropOffs(detail).map(
        makeItineraryItemForGetQuotesV2(
          false,
          codValid,
          isBulkUpload,
          totalWeight,
          itemType,
        ),
      ),
    ],
  };

  const discountInfo = getDiscountInfo(detail);

  const paymentMethodID =
    detail.paymentTypeID === CASH_BY_SENDER ||
    detail.paymentTypeID === CASH_BY_RECIPIENT
      ? ''
      : detail.paymentTypeID;

  const slot = _get(detail, 'configuration.scheduledPickupSlot');
  const scheduledPickupSlot =
    slot && slot.from && slot.to
      ? {
          from: slot.from,
          to: slot.to,
        }
      : undefined;

  const configuration = {
    serviceType: detail.configuration.serviceType,
    specialServiceType: detail.configuration.specialServiceType,
    vehicleType: detail.configuration.vehicleType,
    additionalServices: detail.configuration.additionalServices,
    parcelWeight: totalWeight,
    parcelTotalSize: _get(detail, 'parcelTotalSize'),
    paymentMethodID,
    discountInfo,
    enterprise,
    enableOptimizedRoute,
    scheduledPickupSlot,
    optOutAutoApplyOffer: optOutAutoApplyOffer(detail),
  };

  return {
    baseConfiguration,
    configuration,
  };
};

export const deliveriesToGetQuotesV2Payload = ({
  deliveries,
  cityID,
  enterprise = null,
}) => ({
  cityID,
  deliveries: deliveries.map(deliveryToGetQuoteV2Payload(enterprise)),
});

export const parseItemInfo = (itemType, itemWeight) => {
  const itemTypeName = itemType || '';
  const [catID, catName] = itemTypeName.split('-');
  const parcelWeight = isNaN(itemWeight) ? 0 : +itemWeight;

  return {
    categoryID: catID ? +catID : 0,
    categoryName: catName,
    weight: parcelWeight,
  };
};

const makeDeliveryStepForCreateDeliveriesV2 = (
  isPickUp,
  isBulkUpload,
  totalWeight = 0,
  itemType = undefined,
  insuranceOptions = undefined,
) => (step, index) => {
  const poi = _get(step, 'poi');
  const cod = _get(step, 'cod');
  const insuranceOption = _get(step, 'insuranceOption');

  const poiMeta = _get(poi, 'metadata');
  const searchUUID = _get(poiMeta, 'uuid');
  const searchMetadataAPI = _get(poiMeta, 'api');

  const cashOnDelivery =
    isPickUp || isNaN(cod) || +cod <= 0
      ? undefined
      : {
          enable: true,
          amount: +cod,
        };

  let itemInfo;
  if (!isPickUp) {
    if (isBulkUpload) {
      itemInfo = parseItemInfo(
        _get(step, 'itemType'),
        _get(step, 'itemWeight'),
      );
    } else if (index === 0) {
      itemInfo = parseItemInfo(itemType, totalWeight);
    } else {
      itemInfo = parseItemInfo(itemType, 0);
    }
  }

  // make sure the insurance signature is the correct one from quote (insurance id keeps the same)
  let insuranceSignature;
  const insuranceOptionID = getInsuranceOptionID(insuranceOption);
  if (insuranceOptions && insuranceOptionID !== undefined) {
    insuranceSignature = _get(insuranceOptions, [
      insuranceOptionID,
      'insuranceSignature',
    ]);
  }

  return {
    contact: {
      name: step.name,
      phone: step.phone,
      instruction: step.locationNote,
      driverNotes: step.deliveryInstruction,
    },
    location: {
      address: _get(poi, 'address.combined_address'),
      keywords: _get(poi, 'address.name'),
      coordinates: _get(poi, 'latlng'),
      poiID: _get(poi, 'id'),
      cityID: _get(poi, 'address.cityId'),
      countryID: _get(poi, 'address.country_id'),
      searchUUID,
      searchMetadataAPI,
    },
    jobType: isPickUp ? 0 : 1,
    cashOnDelivery,
    itemInfo,
    insuranceSignature,
  };
};

const deliveryToGetCreateDeliveriesV2Payload = ({
  delivery,
  countryCode,
  paymentTransactionID,
  paymentMethods,
  allTaxiTypes,
  skipDuplicateBookingCheckDeliveryIDs,
  enterprise = null,
}) => {
  const { detail, quote } = delivery;
  const skipDuplicateBookingCheck = _includes(
    skipDuplicateBookingCheckDeliveryIDs,
    delivery.id,
  );

  const isBulkUpload = isBulkUploadDeliveryID(delivery.id);

  const {
    pickUp,
    configuration,
    paymentTypeID,
    paySdkPaymentID,
    paySdkPaymentType,
    paySdkPaymentName,
    itemWeight,
    itemType,
    deliveryInstruction,
    referenceCode,
    promoCode,
    tripCode,
    tripDescription,
    parcelTotalSize,
  } = detail;

  const {
    // serviceType,
    // specialServiceType,
    // vehicleType,
    // additionalServices,
    scheduledPickupSlot,
  } = configuration;

  const appliedDiscount = _get(quote, 'discount');
  const insuranceOptions = _get(quote, 'insurance.options');

  // paySdkPaymentID is the authoritative card ID set directly from PaySDK events.
  // Falls back to paymentTypeID for cash selections and non-PaySDK flows.
  // paySdkPaymentID is cleared by applyPaySdkSelection (on cash) and by a useEffect in
  // payment-details.jsx when showPaySdk turns off, so it is only non-empty when the active
  // payment is a PaySDK card selection.
  const effectivePaymentID = paySdkPaymentID || paymentTypeID;

  const paidBy =
    effectivePaymentID === CASH_BY_SENDER ||
    effectivePaymentID === CASH_BY_RECIPIENT
      ? effectivePaymentID
      : undefined;

  const taxiType = allTaxiTypes.find(tt => tt.ID === quote.taxiTypeID);
  const service = {
    serviceID: quote.taxiTypeID,
    serviceName: _get(taxiType, 'name'),
    expressServiceID: _get(quote, 'expressServiceID'),
    quoteSignature: quote.signature,
    promoCode: appliedDiscount ? promoCode : undefined,
    isPromoApplied: !!appliedDiscount,
    isPromoValid: !!appliedDiscount,
  };

  const paymentType =
    paymentMethods &&
    paymentMethods.find(pm => pm.paymentTypeID === effectivePaymentID);
  const paymentMethod = {
    id: paidBy !== undefined ? '' : effectivePaymentID,
    paidBy,
    name: paySdkPaymentName || _get(paymentType, 'refNumber'),
    type: paySdkPaymentType || _get(paymentType, 'type'),
  };

  const steps = [
    makeDeliveryStepForCreateDeliveriesV2(true, isBulkUpload)(pickUp),
    ...getDeliveryFinalDropOffs(detail).map(
      makeDeliveryStepForCreateDeliveriesV2(
        false,
        isBulkUpload,
        itemWeight,
        itemType,
        insuranceOptions,
      ),
    ),
  ];

  const discountMeta = appliedDiscount
    ? {
        offerID: _get(appliedDiscount, 'id'),
        type: _get(appliedDiscount, 'type'),
        promoCode,
      }
    : undefined;

  const discountInfo = getDiscountInfo(detail, true);

  const tripInfo =
    (!!tripCode || !!tripDescription) && !!enterprise
      ? {
          tripCode,
          tripDescription,
        }
      : undefined;

  return {
    createRequestID: delivery.id,
    service,
    paymentMethod,
    steps,
    noteToDriver: deliveryInstruction,
    discountMeta,
    discountInfo,
    countryCode,
    // string cityCode = 8;
    paidBy,
    referenceCode: referenceCode ? String(referenceCode) : undefined,
    scheduledPickupSlot,
    paymentTransactionID,
    skipDuplicateBookingCheck,
    tripInfo,
    parcelTotalSize,
    ...(isFromMexApp() && getMexCreateDeliveryInfo()),
  };
};

export const deliveriesToCreateDeliveriesV2Payload = ({
  deliveries,
  paymentMethods,
  countryCode,
  paymentTransactionID,
  bookingMode,
  skipDuplicateBookingCheckDeliveryIDs,
  allTaxiTypes = [],
  enterprise = null,
}) => ({
  deliveries: deliveries.map(delivery =>
    deliveryToGetCreateDeliveriesV2Payload({
      delivery,
      countryCode,
      paymentTransactionID,
      paymentMethods,
      allTaxiTypes,
      skipDuplicateBookingCheckDeliveryIDs,
      enterprise,
    }),
  ),
  enterprise,
  bookingMode,
});

export const mergeDisplayError = (errorMessage = {}) => {
  const { title, body, conflictOptions } = errorMessage;
  return {
    reason: title,
    hint: body && `${body} ${conflictOptions && conflictOptions.join(', ')}`,
  };
};

export const isDeliveryUsingOVO = (delivery, countryCode, paymentMethods) => {
  if (!delivery) return false;

  if (countryCode !== 'ID') return false;

  const paymentMethodID = _get(delivery, ['detail', 'paymentTypeID']);
  if (!paymentMethodID) return false;

  const paymentMethod =
    paymentMethods &&
    paymentMethods.find(pm => pm.paymentTypeID === paymentMethodID);
  return _get(paymentMethod, 'type') === PAYMENT_TYPE_GRABPAY;
};

const isEmptyDeliveryDetail = detail =>
  _isEmpty(detail.pickUp) &&
  _isEmpty(detail.dropOffs[0]) &&
  _isEmpty(detail.deliveryInstruction) &&
  _isEmpty(detail.itemType) &&
  _isEmpty(detail.itemWeight) &&
  _isEmpty(detail.paymentTypeID) &&
  _isEmpty(detail.promoCode) &&
  _isEmpty(detail.referenceCode);

export const ajaxFetchDeliveriesQuote = (deliveries, state) => {
  const isPax = isPaxSelector(state);
  const city = selectedCitySelector(state);
  const enterprise = enterpriseSelector(state);

  const quoteRequestConfig = quoteRequestConfigSelector(state);
  const ajaxTimeoutInMs = _get(quoteRequestConfig, 'ajaxTimeoutInMs', 0);

  const payload = deliveriesToGetQuotesV2Payload({
    deliveries,
    cityID: _get(city, 'cityID'),
    enterprise,
  });
  return ajax({
    url: getV2QuotesEpt(),
    method: 'POST',
    headers: buildHeadersV2({
      isPax,
      companyID: _get(enterprise, 'companyID'),
      includeInternalHeaders: true,
    }),
    withCredentials: buildAjaxCredentials(isPax),
    body: JSON.stringify(payload),
    timeout: Math.max(5000, ajaxTimeoutInMs),
  }).pipe(catchError(error => of({ error })));
};

export const isEmptyAllDeliveries = allDeliveries => {
  if (allDeliveries.length > 0) {
    return allDeliveries
      .map(delivery => isEmptyDeliveryDetail(delivery.detail))
      .reduce((res, curr) => res & curr);
  }
  return true;
};

export const truncateText = (text, maxLength = 12) => {
  if (!text) {
    return null;
  }

  if (text.length <= maxLength) {
    return text;
  }
  return `${text.substr(0, maxLength)}...`;
};

export const applyRouteOptimization = (
  oldDelivery,
  routeOptimizationResult,
) => {
  const { OptimizedSteps = [] } = routeOptimizationResult;
  if (_isEmpty(OptimizedSteps)) {
    // / nothing to apply
    return [false, oldDelivery];
  }
  const optimizedSteps = OptimizedSteps.slice(1); // remove pickup
  const oldDetail = oldDelivery.detail;
  const isRoundTrip = oldDetail.isRoundTrip;
  if (isRoundTrip) {
    // / round trip set by flag, `dropOffs` won't contain `pickup`
    optimizedSteps.pop(); // pop the last stop which will be 0
  }
  if (optimizedSteps.length !== oldDetail.dropOffs.length) {
    // / do not apply error state
    return [false, oldDelivery];
  }
  const optimizedDropOffIndexes = optimizedSteps.map((i, _, steps) => {
    if (i === 0) {
      // / for round trip set manually, 0 -> last dropOff
      return steps.length - 1;
    }
    return i - 1;
  });
  const newDropOffs = optimizedDropOffIndexes.map(i => oldDetail.dropOffs[i]);

  const newDeliveryDetail = {
    ...oldDelivery,
    fetchingQuote: false,
    version: oldDelivery.version + 1,
    detail: {
      ...oldDetail,
      dropOffs: newDropOffs,
    },
    routeOptimization: {
      enabled: true,
      preventAutoApplyRO: false,
      ...routeOptimizationResult,
      optimizedDropOffIndexes,
    },
  };
  return [true, newDeliveryDetail];
};

export const mergeDeliveriesIntoMSD = (
  deliveryIDs,
  allDeliveries,
  prefixIfCreatingNew,
  configuration,
) => {
  const newMSDDelivery = _cloneDeep(
    allDeliveries.find(delivery => deliveryIDs[0] === delivery.id),
  );
  if (prefixIfCreatingNew) {
    _set(newMSDDelivery, 'id', genDeliveryID(prefixIfCreatingNew));
    _set(newMSDDelivery, 'quote', undefined);
    _set(newMSDDelivery, 'fareDetailMatrix', undefined);
    _set(newMSDDelivery, 'paxExpressError', undefined);
  }

  if (!_isEmpty(configuration)) {
    newMSDDelivery.detail.configuration = {
      ...newMSDDelivery.detail.configuration,
      ...configuration,
    };
  }

  _set(newMSDDelivery, 'routeOptimization', {
    enabled: true,
    preventAutoApplyRO: false,
  });

  deliveryIDs.forEach((deliveryID, index) => {
    // / Skip the 0 index because we already cloned
    if (index === 0) return;
    const dropOffDelivery = allDeliveries.find(
      delivery => deliveryID === delivery.id,
    );
    const dropOffPOIs = _get(dropOffDelivery, ['detail', 'dropOffs'], []);
    newMSDDelivery.detail.dropOffs.push(...dropOffPOIs);
  });

  return newMSDDelivery;
};

export const getDuplicatedDeliveryIDs = deliveryErrors => {
  const duplicatedDeliveryIDs = [];
  if (!deliveryErrors || _isEmpty(deliveryErrors)) {
    return duplicatedDeliveryIDs;
  }

  Object.entries(deliveryErrors).forEach(([deliveryID, errors]) => {
    if (
      _some(
        errors,
        error => _get(error, 'errorCode') === ErrorCodes.DuplicatedBooking.code,
      )
    ) {
      duplicatedDeliveryIDs.push(deliveryID);
    }
  });
  return duplicatedDeliveryIDs;
};

export default {};
